import dayjs from "dayjs";
import { useFormik } from "formik";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import {
  useFetchDepartmentsQuery,
  useFetchGroupedEquipmentsQuery,
  useFetchTeamsQuery,
  usePostUserMutation,
} from "../../store";
import Loading from "../../components/shared/Loading";
import CustomSelect from "../../components/shared/CustomSelect";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import { useDispatch } from "react-redux";
import {
  openErrorToast,
  openSuccessToast,
} from "../../store/slices/toast/toastSlice";
import CustomDatePicker from "../../components/shared/CustomDatePicker";
import validations from "./validations/createPersonelValidations";
import FormHelperText from "@mui/material/FormHelperText";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import GroupsIcon from "@mui/icons-material/Groups";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import BadgeIcon from "@mui/icons-material/Badge";
import EmailIcon from "@mui/icons-material/Email";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import Divider from "@mui/material/Divider";
import SchoolIcon from "@mui/icons-material/School";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";

const BRAND_RED = "#8B1A1A";
const BRAND_RED_DARK = "#5c0a0a";
const BRAND_RED_LIGHT = "#b94040";

const FieldSection = ({ icon, label, children }) => (
  <Box>
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
      <Box sx={{ color: BRAND_RED, display: "flex" }}>{icon}</Box>
      <Typography variant="caption" fontWeight={700} sx={{ color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8 }}>
        {label}
      </Typography>
    </Stack>
    {children}
  </Box>
);

const CreatePersonel = () => {
  const dispatch = useDispatch();
  const { data: departments, isLoading: departmentsLoading } = useFetchDepartmentsQuery();
  const { data: teamList, isLoading: teamLoading } = useFetchTeamsQuery();
  const { data: equipments, isLoading: equipmentLoading } = useFetchGroupedEquipmentsQuery();
  const [createUser, { isLoading: createUserLoading }] = usePostUserMutation();
  const theme = useTheme();

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: { style: { maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP, width: 250 } },
  };

  function getStyles(name, personName, theme) {
    return {
      fontWeight: personName.includes(name)
        ? theme.typography.fontWeightMedium
        : theme.typography.fontWeightRegular,
    };
  }

  const formik = useFormik({
    initialValues: {
      email: "",
      firstName: "",
      lastName: "",
      departmentId: 0,
      isManager: false,
      isIntern: false,
      workingStartDate: null,
      equipments: [],
      teams: [],
      title: "",
      educationStatus: "",
    },
    validationSchema: validations,
    onSubmit: async (values, bag) => {
      try {
        const model = {
          name: values.firstName,
          lastName: values.lastName,
          email: values.email,
          isManager: false,
          departmentId: values.departmentId,
          id: 0,
          equipments: values.equipments,
          isIntern: values.isIntern,
          workingStartDate:
            values.workingStartDate !== null
              ? dayjs(values.workingStartDate).format("YYYY-MM-DD")
              : null,
          teams: values.teams,
          title: values.title,
          educationStatus: values.educationStatus,
        };
        createUser(model).then((res) => {
          if (res) {
            if (!res.data.isSuccess) {
              dispatch(openErrorToast(res.data.message));
            } else {
              dispatch(openSuccessToast("Kullanıcı Oluşturuldu"));
              formik.resetForm();
            }
          }
        });
      } catch (error) {
        bag.setErrors({ general: error.response.data.message });
      }
    },
  });

  const handleTeamChange = (event) => {
    formik.setFieldValue("teams", event.target.value);
  };
  const handleEquipmentChange = (event) => {
    formik.setFieldValue("equipments", event.target.value);
  };
  const handleChangeDate = ({ name, value }) => {
    const day = new Date(value).getDay();
    if (day === 6 || day === 0) {
      dispatch(openErrorToast("Hafta İçi Seçim Yapınız !"));
      formik.setFieldValue(name, dayjs(null).format("YYYY-MM-DD"));
    } else {
      formik.setFieldValue(name, value ? dayjs(value).format("YYYY-MM-DD") : null);
    }
  };

  if (departmentsLoading || teamLoading || equipmentLoading || createUserLoading)
    return <Loading isLoading={true} />;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        pt: { xs: 3, sm: 4 },
        pb: 2,
        px: { xs: 2, sm: 4 },
        bgcolor: "#f1f5f9",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 900,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(139,26,26,0.18)",
        }}
      >
        {/* Sol Panel */}
        <Box
          sx={{
            background: `linear-gradient(160deg, ${BRAND_RED} 0%, ${BRAND_RED_DARK} 100%)`,
            p: { xs: 4, md: 5 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            color: "#fff",
          }}
        >
          <Box>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <PersonAddAlt1Icon sx={{ fontSize: 30, color: "#fff" }} />
            </Box>
            <Typography variant="h5" fontWeight={800} sx={{ mb: 1.5, lineHeight: 1.2 }}>
              Yeni Personel
              <br />Kaydı
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", lineHeight: 1.7 }}>
              Sisteme yeni bir personel eklemek için formu doldurun. Tüm zorunlu alanları eksiksiz
              girmeniz gerekmektedir.
            </Typography>
          </Box>

          {/* Bilgi Kartları */}
          <Stack spacing={2} sx={{ mt: { xs: 3, md: 0 } }}>
            {[
              { label: "Zorunlu Alanlar", value: "Ad, Soyad, E-posta, Departman" },
              { label: "Opsiyonel", value: "Başlama tarihi, Takım, Zimmet" },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  bgcolor: "rgba(255,255,255,0.1)",
                  borderRadius: 2,
                  p: 2,
                  backdropFilter: "blur(4px)",
                }}
              >
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8 }}>
                  {item.label}
                </Typography>
                <Typography variant="body2" sx={{ color: "#fff", fontWeight: 500, mt: 0.3 }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Sağ Panel — Form */}
        <Box sx={{ bgcolor: "#fff", p: { xs: 2, sm: 3, md: 3.5 } }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, color: "#1e293b", fontSize: "1.1rem" }}>
            Personel Bilgileri
          </Typography>
          <Typography variant="body2" sx={{ color: "#94a3b8", mb: 1.5, fontSize: "0.8rem" }}>
            Aşağıdaki alanları eksiksiz doldurun.
          </Typography>

          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={1.5}>
              {/* Ad & Soyad yan yana */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FieldSection icon={<BadgeIcon fontSize="small" />} label="Ad">
                  <TextField
                    fullWidth
                    name="firstName"
                    id="firstName"
                    size="small"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                    helperText={formik.touched.firstName && formik.errors.firstName}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </FieldSection>
                <FieldSection icon={<BadgeIcon fontSize="small" />} label="Soyad">
                  <TextField
                    fullWidth
                    name="lastName"
                    id="lastName"
                    size="small"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                    helperText={formik.touched.lastName && formik.errors.lastName}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </FieldSection>
              </Box>

              {/* Ünvan & Eğitim yan yana */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FieldSection icon={<WorkOutlineIcon fontSize="small" />} label="Ünvan (Örn: Mühendis)">
                  <TextField
                    fullWidth
                    name="title"
                    id="title"
                    size="small"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    error={formik.touched.title && Boolean(formik.errors.title)}
                    helperText={formik.touched.title && formik.errors.title}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </FieldSection>
                <FieldSection icon={<SchoolIcon fontSize="small" />} label="Eğitim Durumu (Örn: Üniversite 2 Yıllık)">
                  <TextField
                    fullWidth
                    name="educationStatus"
                    id="educationStatus"
                    size="small"
                    value={formik.values.educationStatus}
                    onChange={formik.handleChange}
                    error={formik.touched.educationStatus && Boolean(formik.errors.educationStatus)}
                    helperText={formik.touched.educationStatus && formik.errors.educationStatus}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </FieldSection>
              </Box>

              {/* E-posta */}
              <FieldSection icon={<EmailIcon fontSize="small" />} label="E-Posta">
                <TextField
                  fullWidth
                  name="email"
                  id="email"
                  type="email"
                  placeholder="ad.soyad@wtechin.com"
                  size="small"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </FieldSection>

              {/* Departman & Başlama yan yana */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FieldSection icon={<BusinessIcon fontSize="small" />} label="Departman">
                  <FormControl
                    fullWidth
                    size="small"
                    error={formik.touched.departmentId && Boolean(formik.errors.departmentId)}
                  >
                    <InputLabel htmlFor="departmentId-label">Departman</InputLabel>
                    <CustomSelect
                      data={departments?.data}
                      handleChange={formik.handleChange}
                      id="departmentId"
                      value={formik.values.departmentId}
                    />
                    {formik.touched.departmentId && (
                      <FormHelperText>{formik.errors.departmentId}</FormHelperText>
                    )}
                  </FormControl>
                </FieldSection>
                <FieldSection icon={<CalendarTodayIcon fontSize="small" />} label="İşe Başlama">
                  <CustomDatePicker
                    handleChange={handleChangeDate}
                    value={formik.values.workingStartDate}
                    id="workingStartDate"
                    label="Tarih Seç"
                  />
                </FieldSection>
              </Box>

              <Divider sx={{ borderColor: "#f1f5f9" }} />

              {/* Takım ve Zimmet yan yana */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FieldSection icon={<GroupsIcon fontSize="small" />} label="Takım">
                  <FormControl fullWidth size="small">
                    <InputLabel id="team-label">Takım Seç</InputLabel>
                    <Select
                      labelId="team-label"
                      id="teams"
                      multiple
                      value={formik.values.teams}
                      onChange={handleTeamChange}
                      input={<OutlinedInput label="Takım Seç" sx={{ borderRadius: 2 }} />}
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((id) => {
                            const team = teamList?.data.find((t) => t.id === id);
                            return (
                              <Chip
                                key={id}
                                label={team ? team.name : id}
                                size="small"
                                sx={{ bgcolor: `${BRAND_RED}18`, color: BRAND_RED, fontWeight: 600 }}
                              />
                            );
                          })}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {teamList?.data.map((t) => (
                        <MenuItem key={t.id} value={t.id} style={getStyles(t.name, formik.values.teams, theme)}>
                          {t.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FieldSection>

                <FieldSection icon={<Inventory2Icon fontSize="small" />} label="Zimmet">
                  <FormControl fullWidth size="small">
                    <InputLabel id="equipments-label">Zimmet Ekle</InputLabel>
                    <Select
                      labelId="equipments-label"
                      id="equipments"
                      multiple
                      value={formik.values.equipments}
                      onChange={handleEquipmentChange}
                      input={<OutlinedInput label="Zimmet Ekle" sx={{ borderRadius: 2 }} />}
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((id) => {
                            let equipmentName = id;
                            equipments?.data.forEach((group) => {
                              const eq = group.equipments.find((e) => e.value === id);
                              if (eq) equipmentName = eq.text;
                            });
                            return (
                              <Chip
                                key={id}
                                label={equipmentName}
                                size="small"
                                sx={{ bgcolor: `${BRAND_RED}18`, color: BRAND_RED, fontWeight: 600 }}
                              />
                            );
                          })}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {equipments?.data.map((group) => [
                        <ListSubheader key={group.groupName} sx={{ fontWeight: 700, color: BRAND_RED }}>
                          {group.groupName}
                        </ListSubheader>,
                        group.equipments.map((equipment) => (
                          <MenuItem
                            key={equipment.value}
                            value={equipment.value}
                            style={getStyles(equipment.text, formik.values.equipments, theme)}
                          >
                            {equipment.text}
                          </MenuItem>
                        )),
                      ])}
                    </Select>
                  </FormControl>
                </FieldSection>
              </Box>
              {/* Kaydet Butonu */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                startIcon={<PersonAddAlt1Icon />}
                sx={{
                  mt: 1,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: "1rem",
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${BRAND_RED_LIGHT} 0%, ${BRAND_RED} 60%, ${BRAND_RED_DARK} 100%)`,
                  boxShadow: `0 4px 16px ${BRAND_RED}44`,
                  textTransform: "none",
                  letterSpacing: 0.5,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${BRAND_RED} 0%, ${BRAND_RED_DARK} 100%)`,
                    boxShadow: `0 6px 20px ${BRAND_RED}55`,
                  },
                }}
              >
                Personeli Kaydet
              </Button>
            </Stack>
          </form>
        </Box>
      </Box>
    </Box>
  );
};

export default CreatePersonel;
